import React, {useMemo} from 'react'
import {useFetchEffect} from 'hooks/useFetchEffect'
import Form from 'procedures/changeDependents/add/Form'
import api from 'api'
import {actionCreators, fetchSelector} from 'actions'
import queryString from 'query-string'
import CommentModal from 'procedures/CommentModal'
import {assignTmpFiles, dependentDefaultValue} from 'employees/form/common'
import {validateDependent} from 'employees/form/Dependents'
import {get, set, isEmpty} from 'lodash'
import {toFormValues} from 'utils'
import moment from 'moment'
import {useDispatch, useSelector} from 'react-redux'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {RequireEmployeeUpdateRouter} from 'components/router/RequireEmployeeUpdateRouter'
import {RejectUnemployeeRouter} from 'components/router/RejectUnemployeeRouter'
import {useQuery} from '@apollo/client'
import {CHILDCARE_PROCEDURE_STATUS_ON_ADD_DEPENDENTS, EMPLOYEE_DEPENDENTS} from './query'
import {saveDependentFiles} from 'procedures/enroll/tmpData'
import {notifyError, notifySuccess} from 'store/actions/notify'
import {asyncError} from 'store/actions/asyncError'
import {stopSubmit, SubmissionError, formValueSelector} from 'redux-form'
import {processError} from 'libs/errorHandler'
import {onSubmitFail} from 'jbc-front/components/FormErrors'
import {schema as childcareTmpDataSchema} from 'reportForms/R507Report'

const toString = (value) => {
  return value === null || value === undefined ? undefined : String(value)
}

const toFile = (file) => {
  return file.filename === null && file.url === null ? null : file
}

const formName = 'changeDependents'
const selector = formValueSelector(formName)

// 既存の被扶養者情報を読み込んでフォーム初期値を生成
const LoadFromExistDependents = ({defaultValues, dependentIds, renderForm}) => {
  const employee = useSelector((state) => state.session.currentUser.employee)
  const {loading, data} = useQuery(EMPLOYEE_DEPENDENTS, {
    variables: {dependentIds},
    fetchPolicy: 'network-only',
  })

  const initialValues = useMemo(() => {
    if (loading || !data) {
      return defaultValues
    }

    const dependents = data.me.employee.employeeDependentsWhereByIds.map((dependent) => ({
      ...dependentDefaultValue(),
      id: dependent.id,
      first_name: dependent.firstName,
      last_name: dependent.lastName,
      first_name_kana: dependent.firstNameKana,
      last_name_kana: dependent.lastNameKana,
      birthday: dependent.birthday,
      gender: dependent.gender,
      relation_other: dependent.relationName,
      relation_type: dependent.relationType,
      relation_report_type: dependent.relationReportType,
      residence_status: dependent.residenceStatus,
      domestic_remittance: toString(dependent.domesticRemittance),
      remittance: toString(dependent.remittance),
      prefecture_id: dependent.prefecture?.id,
      postcode_0: dependent.postcode0,
      postcode_1: dependent.postcode1,
      city: dependent.city,
      house_number: dependent.houseNumber,
      building: dependent.building,
      address_kana: dependent.addressKana,
      address_foreign: dependent.addressForeign,
      job: dependent.job,
      phone_number_0: dependent.phoneNumber0,
      phone_number_1: dependent.phoneNumber1,
      phone_number_2: dependent.phoneNumber2,
      basic_pension_number_0: dependent.basicPensionNumber0,
      basic_pension_number_1: dependent.basicPensionNumber1,
      national_type: dependent.nationalType,
      romaji_name: dependent.romajiName,
      romaji_name_kana: dependent.romajiNameKana,
      annual_income: toString(dependent.annualIncome),
      annual_earnings: toString(dependent.annualEarnings),
      handicap_type: dependent.handicapType,
      handicap_detail: dependent.handicapDetail,
      handicap_certificate: toFile(dependent.handicapCertificate),
      is_non_resident: dependent.isNonResident,
      is_study_abroad: dependent.isStudyAbroad,
      related_to_relatives_document: toFile(dependent.relatedToRelativesDocument),
      related_to_remittance_document: toFile(dependent.relatedToRemittanceDocument),
      proving_study_abroad_document: toFile(dependent.provingStudyAbroadDocument),
      dependent_social_insurance: dependent.dependentSocialInsurance,
      dependent_from: dependent.dependentFrom,
      dependent_reason: dependent.dependentReason,
      dependent_tax_law: dependent.dependentTaxLaw,
      dependent_tax_law_from: dependent.dependentTaxLawFrom,
      dependent_tax_law_reason: dependent.dependentTaxLawReason,
      initial_dependent_social_insurance: dependent.dependentSocialInsurance,
      initial_dependent_tax_law: dependent.dependentTaxLaw,
    }))

    return {
      dependents: dependents.length > 0 ? dependents : [dependentDefaultValue()],
      marital_status: employee.marital_status,
      spouse_annual_income: employee.spouse_annual_income,
      spouse_monthly_income: employee.spouse_monthly_income,
    }
  }, [defaultValues, employee, loading, data])

  return renderForm({initialValues})
}

const initialDependentSocialInsurance = (existDependents, addDependent) =>
  existDependents.find((existDependent) => String(existDependent.id) === addDependent.id)?.dependent_social_insurance
const initialDependentTaxLaw = (existDependents, addDependent) =>
  existDependents.find((existDependent) => String(existDependent.id) === addDependent.id)?.dependent_tax_law

// 既存の手続きを読み込んでフォーム初期値を生成 (差し戻し時など)
const LoadFromAddDependentsProcedureStatus = ({defaultValues, procedureStatusId, renderForm}) => {
  const dispatch = useDispatch()
  const token = useSelector((state) => state.auth.token)
  const employee = useSelector((state) => state.session.currentUser.employee)
  const tmpData = useSelector((state) => state.procedureStatuses.tmpData.data)
  const tmpFiles = useSelector((state) => state.procedureStatuses.tmpData.tmpFiles)
  const comments = useSelector((state) => fetchSelector(state, 'procedure_comments').data)
  const existDependents =
    useSelector((state) => state.procedureStatuses.current.data.employee?.employee_dependents) || []

  useFetchEffect(() => {
    // load procedureStatus
    dispatch(
      actionCreators.procedureStatuses.current.fetchData(
        api.createWithAuth(token).procedureStatuses.get(procedureStatusId, ['employee', 'employee_dependents'])
      )
    )
    // load tmpData
    dispatch(
      actionCreators.procedureStatuses.tmpData.fetchData(
        Promise.all([
          api
            .createWithAuth(token)
            .procedureStatuses.tmpData.mapToJson(procedureStatusId, {name: 'procedure_add_dependents'}),
          api.createWithAuth(token).procedureStatuses.tmpFiles.list(procedureStatusId, 'application'),
        ]).then((result) => Object.assign(...result))
      )
    )
    // load comments
    dispatch(
      actionCreators.fetchData(
        'procedure_comments',
        api.createWithAuth(token).procedureStatuses.procedureComments.list(procedureStatusId)
      )
    )

    return () => {
      dispatch(actionCreators.procedureStatuses.current.destroy())
      dispatch(actionCreators.procedureStatuses.tmpData.destroy())
      dispatch(actionCreators.fetchDestroy('procedure_comments'))
    }
  }, [procedureStatusId])

  const initialValues = useMemo(() => {
    if (!tmpData || !tmpFiles) {
      return defaultValues
    }

    const addDependents = tmpData.application_procedure_add_dependents
    const dependents = addDependents
      ? addDependents.employee_dependents.map((dependent) => ({
          ...dependentDefaultValue(),
          ...dependent,
          initial_dependent_social_insurance: initialDependentSocialInsurance(existDependents, dependent),
          initial_dependent_tax_law: initialDependentTaxLaw(existDependents, dependent),
        }))
      : []

    const dependentsFormValues = {
      dependents: dependents.length > 0 ? dependents : [dependentDefaultValue()],
      marital_status: employee.marital_status,
      spouse_annual_income: employee.spouse_annual_income,
      spouse_monthly_income: employee.spouse_monthly_income,
      ...toFormValues(addDependents),
    }

    return assignTmpFiles({...dependentsFormValues}, tmpFiles)
  }, [defaultValues, employee, tmpData, tmpFiles, existDependents])

  return renderForm({initialValues, comments, tmpFiles})
}

// 産休の手続きを読み込んでフォーム初期値を生成
const LoadFromMaternityProcedureStatus = ({defaultValues, maternityProcedureStatusId, renderForm}) => {
  const dispatch = useDispatch()
  const token = useSelector((state) => state.auth.token)
  const employee = useSelector((state) => state.session.currentUser.employee)
  const tmpData = useSelector((state) => state.procedureStatuses.tmpData.data)

  useFetchEffect(() => {
    // load maternityProcedureStatus
    dispatch(
      actionCreators.procedureStatuses.tmpData.fetchData(
        api.createWithAuth(token).procedureStatuses.tmpData.mapToJson(maternityProcedureStatusId, {
          type: 'application',
          name: 'procedure_start_maternity',
        })
      )
    )

    return () => {
      dispatch(actionCreators.procedureStatuses.tmpData.destroy())
    }
  }, [maternityProcedureStatusId])

  const initialValues = useMemo(() => {
    if (!tmpData) {
      return defaultValues
    }

    const procedureStartMaternity = tmpData.application_procedure_start_maternity
    const dependents = procedureStartMaternity
      ? procedureStartMaternity.procedure_maternity_child_data.map((dependent) => ({
          ...dependentDefaultValue(),
          first_name: dependent.first_name,
          first_name_kana: dependent.first_name_kana,
          last_name: dependent.last_name,
          last_name_kana: dependent.last_name_kana,
          birthday: dependent.birth_date,
          dependent_reason: '出生',
          relation_type: 'other',
        }))
      : []

    return {
      dependents: dependents.length > 0 ? dependents : [dependentDefaultValue()],
      date_on: procedureStartMaternity?.expected_birth_date,
      marital_status: employee.marital_status,
      spouse_annual_income: employee.spouse_annual_income,
      spouse_monthly_income: employee.spouse_monthly_income,
    }
  }, [defaultValues, employee, tmpData])

  return renderForm({initialValues})
}

// 育休の手続きを読み込んでフォーム初期値を生成
const LoadFromChildcareProcedureStatus = ({defaultValues, childcareProcedureStatusId, renderForm}) => {
  const employee = useSelector((state) => state.session.currentUser.employee)
  const {loading, data} = useQuery(CHILDCARE_PROCEDURE_STATUS_ON_ADD_DEPENDENTS, {
    variables: {procedureStatusId: childcareProcedureStatusId},
    fetchPolicy: 'network-only',
  })

  const initialValues = useMemo(() => {
    if (loading || !data) {
      return defaultValues
    }

    const tmpData = childcareTmpDataSchema.validateSync(data.client.procedureStatus.application.data, {strict: true})
    const child = tmpData.childcareChildDatum
    const dependent = {
      ...dependentDefaultValue(),
      first_name: child.firstName,
      first_name_kana: child.firstNameKana,
      last_name: child.lastName,
      last_name_kana: child.lastNameKana,
      birthday: child.birthDate,
      dependent_reason: '出生',
      relation_type: 'other',
    }

    return {
      dependents: [dependent],
      date_on: child.birthDate,
      marital_status: employee.marital_status,
      spouse_annual_income: employee.spouse_annual_income,
      spouse_monthly_income: employee.spouse_monthly_income,
    }
  }, [defaultValues, employee, loading, data])

  return renderForm({initialValues})
}

const hasDuplicateSpouse = (dependents, existSpouse) => {
  // 二人以上の配偶者情報が入力されている場合
  if (dependents.filter((dependent) => dependent.relation_type === 'spouse').length > 1) {
    return true
  }

  if (!existSpouse) return false

  const inputDependents = {
    others: [],
    spouse: null,
  }
  dependents.forEach((dependent) => {
    if (dependent.relation_type === 'spouse') {
      inputDependents.spouse = dependent
    } else {
      inputDependents.others.push(dependent)
    }
  })

  if (inputDependents.spouse) {
    const inputSpouseId = inputDependents.spouse.id
    // 登録済みの配偶者が存在する状態で、新しく配偶者情報が入力されている
    if (!inputSpouseId) {
      return true
    }

    const existSpouseId = String(existSpouse.id)

    // 登録済みの配偶者が存在する状態で、既存のその他家族を配偶者に変更している && 登録済みの配偶者をその他に変更していない
    if (existSpouseId !== inputSpouseId && !inputDependents.others.map((dep) => dep.id).includes(existSpouseId)) {
      return true
    }
  }

  return false
}

// MEMO: このコンポーネントは管理者は利用しない想定
export const AddEmployeeDependents = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const healthInsuranceType = useSelector((state) =>
    get(state.session.currentUser, 'employee.office.health_insurance_type')
  )
  const token = useSelector((state) => state.auth.token)
  const employee = useSelector((state) => state.session.currentUser.employee)
  const clientRoleType = useSelector((state) => state.session.currentUser.client_role.client_role_type)

  const formValueDependents = useSelector((state) => selector(state, 'dependents')) || []
  const needInputDependents =
    healthInsuranceType === 'its' &&
    employee.health_insurance?.joined &&
    !isEmpty(formValueDependents.filter((dependent) => dependent.dependent_social_insurance))

  const {id: procedureStatusId} = useParams()
  const {search} = useLocation()
  const {
    dependentIds,
    maternity_procedure_status_id: maternityProcedureStatusId,
    childcare_procedure_status_id: childcareProcedureStatusId,
  } = queryString.parse(search, {
    arrayFormat: 'bracket',
  })
  const defaultValues = useMemo(
    () => ({
      dependents: [dependentDefaultValue()],
      marital_status: employee.marital_status,
      spouse_annual_income: employee.spouse_annual_income,
      spouse_monthly_income: employee.spouse_monthly_income,
    }),
    [employee]
  )

  const handleSubmit = async (values, comment, tmpFiles = []) => {
    const {date_on: dateOn, dependents, ...rest} = values
    const authedApi = api.createWithAuth(token)
    try {
      const {data: employeeData} = await authedApi.employees.getCurrent(['employee_dependents'])
      const existSpouse = employeeData.employee_dependents.find((dependent) => dependent.relation_type === 'spouse')

      if (hasDuplicateSpouse(dependents, existSpouse)) {
        dispatch(notifyError('配偶者を2人以上登録する事はできません。'))
        return
      }

      // 被扶養者の異動日チェック
      const dateOnMoment = moment(dateOn, 'YYYY/MM/DD')
      if (dependents.some((dependent) => dateOnMoment.isBefore(moment(dependent.birthday, 'YYYY/MM/DD')))) {
        dispatch(notifyError('被扶養者の生年月日を異動日より未来に設定する事はできません。'))
        return
      }

      // procedureStatusを作成してIDを返す
      const createProcedureStatusId = async () => {
        const {data} = await authedApi.procedureStatuses.start(
          employeeData.id,
          'add_dependents',
          dateOn,
          'employee_draft',
          false
        )
        return data.id
      }

      const targetProcedureStatusId = procedureStatusId || (await createProcedureStatusId())

      await authedApi.procedureStatuses.tmpData.updateJson(
        targetProcedureStatusId,
        'application',
        'procedure_add_dependents',
        (data = {employee_dependents: [], report_params_sets: {}}) => ({
          ...data,
          date_on: dateOn,
          employee_dependents: dependents,
          ...rest,
        })
      )

      await saveDependentFiles({
        authedApi,
        procedureStatusId: targetProcedureStatusId,
        dependents,
        procedureTmpType: 'application',
        tmpFiles,
      }).catch((err) => {
        processError(err)
      })

      if (needInputDependents) {
        // IT健保の場合
        history.push(`/mypage/add_dependents/employee_input_dependents/${targetProcedureStatusId}?dependent_index=0`)
        dispatch(notifySuccess('保存しました'))
      } else {
        await authedApi.procedureStatuses.apply(targetProcedureStatusId, comment)
        history.push('/dashboard')
        dispatch(notifySuccess('申請しました'))
      }
    } catch (err) {
      if (err instanceof SubmissionError) {
        onSubmitFail(err.errors, dispatch)
        dispatch(stopSubmit('changeDependents', err.errors))
      } else {
        dispatch(asyncError(err))
      }
    }
  }
  const validate = (values) => {
    const {dependents, date_on} = values
    if (!dependents) {
      return undefined
    }

    const errors = {dependents: []}

    const needDateOn = dependents.some(
      (dependent) => dependent.dependent_social_insurance || dependent.dependent_tax_law
    )
    if (needDateOn && !date_on) {
      errors.date_on = 'を入力してください'
    }

    dependents.forEach((dependent, index) => {
      const dependentErrors = validateDependent(dependent)

      if (Object.keys(dependentErrors).length > 0) {
        Object.entries(dependentErrors).forEach(([field, message]) => {
          set(errors.dependents, `[${index}].${field}`, message)
        })
      }
    })

    return isEmpty(errors) ? undefined : errors
  }

  const renderForm = ({initialValues, comments, tmpFiles}) => (
    <CommentModal
      formName="changeDependents"
      procedureType="changeDependents"
      onSubmit={({values, comment}) => handleSubmit(values, comment, tmpFiles)}
      comments={comments || []}
    >
      {({showModal}) => (
        <Form
          onSubmit={needInputDependents ? (values) => handleSubmit(values, undefined, tmpFiles) : showModal}
          initialValues={initialValues}
          submitText={healthInsuranceType === 'its' ? '次へ' : '申請'}
          validate={validate}
          comments={comments || {}}
        />
      )}
    </CommentModal>
  )

  return (
    <RequireEmployeeUpdateRouter employeeId={employee.id} redirectPath="/dashboard?error=employee_info">
      <RejectUnemployeeRouter
        clientRoleType={clientRoleType}
        employmentStatus={employee?.employment_status}
        redirectPath="/dashboard?error=employee_info"
      >
        {procedureStatusId ? (
          <LoadFromAddDependentsProcedureStatus
            defaultValues={defaultValues}
            procedureStatusId={procedureStatusId}
            renderForm={renderForm}
          />
        ) : dependentIds ? (
          <LoadFromExistDependents defaultValues={defaultValues} dependentIds={dependentIds} renderForm={renderForm} />
        ) : maternityProcedureStatusId ? (
          <LoadFromMaternityProcedureStatus
            defaultValues={defaultValues}
            maternityProcedureStatusId={maternityProcedureStatusId}
            renderForm={renderForm}
          />
        ) : childcareProcedureStatusId ? (
          <LoadFromChildcareProcedureStatus
            defaultValues={defaultValues}
            childcareProcedureStatusId={childcareProcedureStatusId}
            renderForm={renderForm}
          />
        ) : (
          renderForm({initialValues: defaultValues})
        )}
      </RejectUnemployeeRouter>
    </RequireEmployeeUpdateRouter>
  )
}
